import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { AccordionCohabitationVM } from 'cohabitation/view-models/detail-cohabitation';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { Visibility } from '@mui/icons-material';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';
import { DateTime } from 'luxon';
import { noop } from 'shell/utils/noop';
import Table from 'shell/components/shared/table';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { FormikErrors, FormikTouched } from 'formik';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';

interface FormCohabitatonProps {
  disabled?: boolean;
  values?: AccordionCohabitationVM;
  isEditMode?: boolean;
  isCancellation?: boolean;
  onChange: (
    key: keyof AccordionCohabitationVM,
    value: AccordionCohabitationVM[keyof AccordionCohabitationVM]
  ) => void;
  errors?: FormikErrors<AccordionCohabitationVM>;
  touched?: FormikTouched<AccordionCohabitationVM>;
}

interface CohabitationMemberVM extends BaseTableRows {
  surname?: string;
  name?: string;
  fiscalCode?: string;
  birthday?: string;
  parenthood?: string;
  action?: JSX.Element;
}

const headCells: HeadCell<CohabitationMemberVM, keyof CohabitationMemberVM>[] = [
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'parenthood',
    label: 'Parentela',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

const FormCohabitatonComponent = ({
  disabled,
  values,
  onChange,
  isEditMode,
  errors,
  isCancellation,
  touched,
}: FormCohabitatonProps): React.ReactElement => {
  const [queryParams] = useSearchParams();
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const anprId = queryParams.get('anprId') || '';

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<CohabitationMemberVM>>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            identificativoANPR: anprId,
            tipoScheda: '2',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: (response) => {
        const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];
        const persons = subjects.map((subject) => {
          return {
            surname: subject.generalita?.cognome,
            name: subject.generalita?.nome,
            fiscalCode: subject.generalita?.codiceFiscale?.codFiscale,
            birthday: subject.generalita?.dataNascita
              ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
              : '',
            parenthood: subject.legameSoggetto?.descrizioneCodiceLegame, // Da verificare
          };
        });

        return {
          persons,
          warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
            (warning) => ({
              id: warning.codiceErroreAnomalia,
              value: warning.testoErroreAnomalia,
            })
          ),
          errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
            id: error.codiceErroreAnomalia,
            value: error.testoErroreAnomalia,
          })),
        };
      },
    }
  );

  const navigate = useNavigate();

  const castedAnprData = anpr.data as AnprVM<CohabitationMemberVM>;

  const dataPersons: Array<CohabitationMemberVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((subject) => ({
        fiscalCode: subject?.fiscalCode || '',
        surname: subject?.surname || '',
        name: subject?.name || '',
        birthday: subject?.birthday || '',
        parenthood: subject?.parenthood || '',
        action: (
          <IconButton
            // onClick={() => handleClickSelect(subject)} // TODO onClick
            onClick={() =>
              navigate({
                pathname: '/persona/dettaglio',
                search: `?${createSearchParams({
                  codiceFiscale: subject.fiscalCode || '',
                  redirectUri: `/convivenza/ricerca/${
                    isCancellation ? 'cancellazione' : isEditMode ? 'modifica' : 'dettaglio'
                  }?ricercaNazionale=${nationalSearch}&anprId=${anprId}`,
                })}`,
              })
            }
            id={subject?.fiscalCode}
          >
            <Visibility />
          </IconButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, navigate, isCancellation, isEditMode, anprId, nationalSearch]);

  const toPrintCSV = useMemo(() => {
    const toPrint = dataPersons.map((person) => {
      return {
        cognome: person.surname || '',
        nome: person.name || '',
        CF: person.fiscalCode || '',
        dataNascita: person.birthday || '',
        parentela: person.parenthood || '',
      };
    });

    return toPrint;
  }, [dataPersons]);

  return (
    <Grid container spacing={2}>
      <Grid item container spacing={2} xs={12}>
        <Grid item xs={12} md={3}>
          <PmiTextField
            aria-label="Identificativo Comune"
            label="Identificativo Comune"
            id="municipalityIdentifier"
            name="municipalityIdentifier"
            fullWidth
            disabled={isEditMode ? false : disabled}
            type="text"
            onChange={(event) => {
              onChange('municipalityIdentifier', event.target.value);
            }}
            variant="outlined"
            value={values?.municipalityIdentifier || ''}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            aria-label="Codice ISTAT"
            label="Codice ISTAT"
            id="istatCode"
            name="istatCode"
            fullWidth
            type="text"
            disabled={isEditMode ? false : disabled}
            onChange={(event) => {
              onChange('istatCode', event.target.value);
            }}
            variant="outlined"
            value={values?.istatCode || ''}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            aria-label="Identificativo ANPR"
            label="Identificativo ANPR"
            id="idANPR"
            name="idANPR"
            fullWidth
            type="text"
            disabled
            onChange={(event) => {
              onChange('idANPR', event.target.value);
            }}
            variant="outlined"
            value={values?.idANPR || ''}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12}>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data origine"
            value={values?.originDate || null}
            disabled={isEditMode ? false : disabled}
            onChange={(dataValue) => {
              onChange('originDate', dataValue || null);
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="originDate"
                fullWidth
                error={touched?.originDate && Boolean(errors?.originDate)}
                helperText={errors?.originDate}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          aria-label="Specie convivenza"
          label="Specie convivenza"
          id="kindCohabitation"
          name="kindCohabitation"
          disabled={isEditMode ? false : disabled}
          fullWidth
          type="text"
          onChange={(event) => {
            onChange('kindCohabitation', event.target.value);
          }}
          variant="outlined"
          value={values?.kindCohabitation || ''}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          aria-label="Denominazione convivenza"
          label="Denominazione convivenza"
          id="nameCohabitation"
          name="nameCohabitation"
          disabled={isEditMode ? false : disabled}
          fullWidth
          type="text"
          onChange={(event) => {
            onChange('nameCohabitation', event.target.value);
          }}
          error={touched?.nameCohabitation && Boolean(errors?.nameCohabitation)}
          helperText={errors?.nameCohabitation}
          variant="outlined"
          value={values?.nameCohabitation || ''}
        />
      </Grid>
      {dataPersons.length > 0 ? (
        <Grid item container spacing={1} xs={12}>
          <Grid item xs={12}>
            <PmiTitleDivider>Componenti della convivenza</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <CsvExtraction dataToExtract={toPrintCSV} title="Lista_Componenti" />
          </Grid>
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={dataPersons}
              count={dataPersons.length}
              head={headCells}
              hideButtons
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
            />
          </Grid>
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormCohabitatonComponent.displayName = 'Form cohabitation component';

const FormCohabitaton = memo(FormCohabitatonComponent);

export default FormCohabitaton;
